import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router';
import { t } from '../i18n';
import { useIngestion, useIngestions } from '../lib/queries';
import { fmtDateTime, fmtRelative } from '../lib/format';
import { Confidence, Empty, ErrorState, Ltr } from '../components/ui';
import { ResolveForm } from '../components/ResolveForm';
import { FilePreview } from '../components/DocumentDrawer';
import { IconExternal } from '../components/icons';

export function ReviewPage() {
  const [params, setParams] = useSearchParams();
  const { data, isLoading, error, refetch } = useIngestions({ status: 'needs_review', pageSize: 200 });
  const selectedId = Number(params.get('item')) || data?.items[0]?.id || null;
  const { data: item } = useIngestion(selectedId);
  const [view, setView] = useState<'form' | 'file'>('form');

  const select = (id: number | null) => {
    const next = new URLSearchParams(params);
    if (id) next.set('item', String(id));
    else next.delete('item');
    setParams(next, { replace: true });
  };

  // After resolving, move to the next item in the queue.
  const advance = () => {
    if (!data) return;
    const idx = data.items.findIndex((i) => i.id === selectedId);
    const next = data.items[idx + 1] ?? data.items[idx - 1];
    select(next && next.id !== selectedId ? next.id : null);
  };

  useEffect(() => setView('form'), [selectedId]);

  return (
    <>
      <div className="page-head">
        <div>
          <h1>{t.review.title}</h1>
          <div className="sub">{t.review.sub}</div>
        </div>
      </div>

      {error ? (
        <div className="panel"><ErrorState error={error} onRetry={() => refetch()} /></div>
      ) : data && data.items.length === 0 ? (
        <div className="panel">
          <Empty title={t.review.empty.title} body={t.review.empty.body} />
        </div>
      ) : (
        <div className="split">
          <div className="split-list" role="listbox" aria-label={t.review.title}>
            {isLoading
              ? Array.from({ length: 6 }, (_, i) => (
                  <div key={i} className="queue-item"><span className="skeleton" style={{ width: '70%' }} /></div>
                ))
              : data?.items.map((i) => (
                  <button key={i.id} role="option" aria-selected={i.id === selectedId} className={`queue-item ${i.id === selectedId ? 'active' : ''}`} onClick={() => select(i.id)}>
                    <div className="row">
                      <span className="strong">{i.detectedTypeLabel ?? 'מסמך'} <Ltr className="tabular">{i.detectedNumber ?? ''}</Ltr></span>
                      <span className="spacer" />
                      <span className="small faint">{fmtRelative(i.receivedAt ?? i.createdAt)}</span>
                    </div>
                    <div className="small ellipsis" style={{ marginTop: 2 }}>{i.extracted?.customerName ?? <span className="muted">לקוח לא זוהה</span>}</div>
                    <div className="row small muted" style={{ marginTop: 4 }}>
                      {i.candidates[0] ? (
                        <>
                          <span className="ellipsis" style={{ maxWidth: 190 }}>הצעה: {i.candidates[0].businessName}</span>
                          <span className="spacer" />
                          <Confidence value={i.candidates[0].score} />
                        </>
                      ) : (
                        <span>אין לקוח מתאים — לקוח חדש?</span>
                      )}
                    </div>
                  </button>
                ))}
          </div>

          <div className="split-detail">
            {!item ? (
              <div className="panel-body"><span className="skeleton" style={{ width: '50%' }} /></div>
            ) : (
              <>
                <div className="panel-head">
                  <div style={{ minWidth: 0 }}>
                    <h2 className="ellipsis">{item.detectedTypeLabel ?? 'מסמך'} <Ltr>{item.detectedNumber ?? ''}</Ltr></h2>
                    <div className="small muted ellipsis">
                      {item.source === 'manual' ? 'הועלה ידנית' : item.subject} · {fmtDateTime(item.receivedAt ?? item.createdAt)}
                    </div>
                  </div>
                  <span className="spacer" />
                  <div className="segmented mobile-only">
                    <button className={view === 'form' ? 'active' : ''} onClick={() => setView('form')}>פרטים</button>
                    <button className={view === 'file' ? 'active' : ''} onClick={() => setView('file')}>מסמך</button>
                  </div>
                  {item.gmailUrl ? (
                    <a className="btn sm" href={item.gmailUrl} target="_blank" rel="noreferrer noopener">
                      <IconExternal /> {t.docs.openEmail}
                    </a>
                  ) : null}
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1fr) minmax(0, 1fr)', flex: 1, minHeight: 0 }} className="doc-layout">
                  <div style={{ overflowY: 'auto', padding: 16, maxHeight: 'calc(100vh - 220px)' }} className={view === 'file' ? 'desktop-only' : ''}>
                    <ResolveForm ingestion={item} onDone={advance} onIgnore={advance} />
                  </div>
                  <div style={{ background: '#e9e9e5', minHeight: 480 }} className={view === 'form' ? 'desktop-only' : ''}>
                    <FilePreview file={item.fileId ? { id: item.fileId, mimeType: item.fileMime ?? 'application/pdf' } : null} />
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </>
  );
}
